/**
 * A resource's contract window, synced read-only from Resource Manager.
 *
 * Either bound may be missing: with only a start the resource is bookable from
 * that day on, with only an end up to and including that day, and with neither
 * the contract places no restriction (the working profile still applies).
 * Bounds are 'yyyy-MM-dd' day keys and are compared as strings, so a date-only
 * value never shifts a day in a negative UTC offset.
 */
import type { Resource } from '@/types';

type ContractResource = Pick<Resource, 'contractStartDate' | 'contractEndDate'>;

const DAY_KEY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})/;

/** The 'yyyy-MM-dd' part of an API date ('2027-03-31' or '2027-03-31T00:00:00'). */
export function toContractDayKey(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  const match = DAY_KEY_PATTERN.exec(value);
  return match ? `${match[1]}-${match[2]}-${match[3]}` : undefined;
}

function dayKey(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/** 'dd/MM/yyyy' read from a 'yyyy-MM-dd' key. */
export function formatContractDay(key: string): string {
  const parsed = DAY_KEY_PATTERN.exec(key);
  return parsed ? `${parsed[3]}/${parsed[2]}/${parsed[1]}` : key;
}

function dayKeyToLocalDate(key: string): Date {
  const [year, month, day] = key.split('-').map(Number);
  return new Date(year, month - 1, day);
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function dayAfter(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1);
}

export function hasContractWindow(resource: ContractResource | null | undefined): boolean {
  return Boolean(resource?.contractStartDate || resource?.contractEndDate);
}

export function isDayWithinContract(resource: ContractResource | null | undefined, key: string): boolean {
  if (!resource) return true;
  if (resource.contractStartDate && key < resource.contractStartDate) return false;
  if (resource.contractEndDate && key > resource.contractEndDate) return false;
  return true;
}

export function isDateWithinContract(resource: ContractResource | null | undefined, date: Date): boolean {
  return isDayWithinContract(resource, dayKey(date));
}

/**
 * The first day the booking covers that falls outside the contract, or null
 * when every covered day is inside it. The end is exclusive, so a booking
 * ending at midnight does not cover the next day.
 */
export function firstDayOutsideContract(
  resource: ContractResource | null | undefined,
  startTime: Date,
  endTime: Date,
): string | null {
  if (!resource || !hasContractWindow(resource)) return null;
  if (isNaN(startTime.getTime()) || isNaN(endTime.getTime())) return null;
  const firstKey = dayKey(startTime);
  const lastKey = endTime > startTime ? dayKey(new Date(endTime.getTime() - 1)) : firstKey;
  if (resource.contractStartDate && firstKey < resource.contractStartDate) return firstKey;
  if (resource.contractEndDate && lastKey > resource.contractEndDate) {
    const dayAfterEnd = dayKey(dayAfter(dayKeyToLocalDate(resource.contractEndDate)));
    return firstKey > dayAfterEnd ? firstKey : dayAfterEnd;
  }
  return null;
}

/** Recurrence dates split into those inside the contract and those cut off. */
export function clampDatesToContract<T extends { start: Date; end: Date }>(
  resource: ContractResource | null | undefined,
  occurrences: T[],
): { kept: T[]; dropped: T[] } {
  if (!resource || !hasContractWindow(resource)) return { kept: occurrences, dropped: [] };
  const kept: T[] = [];
  const dropped: T[] = [];
  occurrences.forEach(occurrence => {
    if (firstDayOutsideContract(resource, occurrence.start, occurrence.end)) {
      dropped.push(occurrence);
    } else {
      kept.push(occurrence);
    }
  });
  return { kept, dropped };
}

/** 'Contract 01/04/2026 – 31/03/2027', 'Contract ends 31/03/2027', or null. */
export function describeContractWindow(resource: ContractResource | null | undefined): string | null {
  const start = resource?.contractStartDate;
  const end = resource?.contractEndDate;
  if (start && end) return `Contract ${formatContractDay(start)} – ${formatContractDay(end)}`;
  if (start) return `Contract starts ${formatContractDay(start)}`;
  if (end) return `Contract ends ${formatContractDay(end)}`;
  return null;
}

export interface OutOfContractRange {
  kind: 'before' | 'after';
  startDate: Date;
  endDate: Date;
  label: string;
}

/**
 * The parts of the visible range outside the contract: at most one range
 * before the contract start and one after the contract end, clamped to the
 * visible range.
 */
export function outOfContractRanges(
  resource: ContractResource | null | undefined,
  visibleStart: Date,
  visibleEnd: Date,
): OutOfContractRange[] {
  if (!resource || !hasContractWindow(resource)) return [];
  const ranges: OutOfContractRange[] = [];
  const rangeStart = startOfDay(visibleStart);
  const rangeEnd = visibleEnd;

  if (resource.contractStartDate) {
    const contractStart = dayKeyToLocalDate(resource.contractStartDate);
    if (contractStart > rangeStart) {
      ranges.push({
        kind: 'before',
        startDate: rangeStart,
        endDate: contractStart < rangeEnd ? contractStart : rangeEnd,
        label: `Contract starts ${formatContractDay(resource.contractStartDate)}`,
      });
    }
  }

  if (resource.contractEndDate) {
    const afterEnd = dayAfter(dayKeyToLocalDate(resource.contractEndDate));
    if (afterEnd < rangeEnd) {
      ranges.push({
        kind: 'after',
        startDate: afterEnd > rangeStart ? afterEnd : rangeStart,
        endDate: rangeEnd,
        label: `Contract ended ${formatContractDay(resource.contractEndDate)}`,
      });
    }
  }

  return ranges;
}
